import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

const OnboardingOverlay = ({ 
  isActive, 
  currentStep, 
  onNext, 
  onPrevious,
  onSkip, 
  onComplete,
  steps = [],
  elementRefs = {},
  containerRef = null
}) => {
  const { t } = useTranslation();
  const [isScrolling, setIsScrolling] = useState(false);
  const [highlightPosition, setHighlightPosition] = useState(null);
  const animationFrameRef = useRef(null);

  // Check if element is in viewport
  const isElementInViewport = useCallback((element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= windowHeight &&
      rect.right <= windowWidth
    );
  }, []);

  // Update highlight position continuously
  const updateHighlightPosition = useCallback(() => {
    if (!isActive) return;

    const currentStepData = steps[currentStep];
    if (!currentStepData?.elementRef || !elementRefs[currentStepData.elementRef]?.current) {
      setHighlightPosition(null);
      return;
    }

    const element = elementRefs[currentStepData.elementRef].current;
    const elementRect = element.getBoundingClientRect();
    
    // Get container bounds if available, otherwise use viewport
    let containerRect = { top: 0, left: 0 };
    if (containerRef?.current) {
      containerRect = containerRef.current.getBoundingClientRect();
    }
    
    // Calculate position relative to container
    const newPosition = {
      top: elementRect.top - containerRect.top - 4,
      left: elementRect.left - containerRect.left - 4,
      width: elementRect.width + 8,
      height: elementRect.height + 8,
    };

    setHighlightPosition(newPosition);
    
    // Continue updating on next frame
    animationFrameRef.current = requestAnimationFrame(updateHighlightPosition);
  }, [isActive, currentStep, steps, elementRefs, containerRef]);

  // Auto-scroll to element if not in viewport
  const scrollToElement = useCallback((elementRef) => {
    const element = elementRef?.current;
    if (!element) return;

    if (!isElementInViewport(element)) {
      setIsScrolling(true);
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'center'
      });
      
      // Wait for scroll to complete
      setTimeout(() => {
        setIsScrolling(false);
      }, 800);
    }
  }, [isElementInViewport]);

  // Handle step changes with auto-scroll
  useEffect(() => {
    if (isActive && steps[currentStep]?.elementRef) {
      const elementRef = elementRefs[steps[currentStep].elementRef];
      scrollToElement(elementRef);
    }
  }, [currentStep, isActive, steps, elementRefs, scrollToElement]);

  // Start/stop continuous position updates
  useEffect(() => {
    if (isActive) {
      updateHighlightPosition();
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setHighlightPosition(null);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isActive, updateHighlightPosition]);

  if (!isActive) return null;

  const currentStepData = steps[currentStep];
  if (!currentStepData) return null;

  // Get tooltip position with improved bounds checking
  const getTooltipPosition = () => {
    const tooltip = currentStepData.tooltip || { top: '50%', left: '50%' };
    
    // Convert percentage to pixels for bounds checking
    const topPercent = parseFloat(tooltip.top) / 100;
    const leftPercent = parseFloat(tooltip.left) / 100;
    
    // Ensure tooltip doesn't go off-screen with more conservative bounds
    const adjustedTop = Math.max(15, Math.min(85, topPercent * 100));
    const adjustedLeft = Math.max(15, Math.min(85, leftPercent * 100));
    
    return {
      top: `${adjustedTop}%`,
      left: `${adjustedLeft}%`,
    };
  };

  const tooltipPosition = getTooltipPosition();

  return (
    <>
      {/* Highlight box - positioned relative to container */}
      {highlightPosition && (
        <div 
          className="absolute border-2 border-primary bg-primary/10 rounded-xl shadow-lg z-50"
          style={{
            top: highlightPosition.top,
            left: highlightPosition.left,
            width: highlightPosition.width,
            height: highlightPosition.height,
            pointerEvents: 'none', // Allow clicks to pass through to elements
          }}
        />
      )}
      
      {/* Overlay for tooltip only */}
      <div className="absolute inset-0 z-50 pointer-events-auto">
        {/* Semi-transparent overlay */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Tooltip */}
        <div 
          className="absolute bg-white rounded-xl shadow-soft p-4 left-1/2"
          style={{
            top: tooltipPosition.top,
            transform: `translate(-50%, -50%)`,
            maxHeight: '80vh',
            overflowY: 'auto',
            width: 'calc(100% - 3rem)',
            maxWidth: '400px',
          }}
        >
          <div className="text-sm sm:text-base text-gray-700 mb-3 leading-relaxed">
            {currentStepData.content}
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {currentStep + 1} of {steps.length}
            </span>
            
            <div className="flex gap-2">
              <button
                onClick={onSkip}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors uppercase"
              >
                {t('common.skip')}
              </button>
              {currentStep > 0 && (
                <button
                  onClick={onPrevious}
                  className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {t('common.previous')}
                </button>
              )}
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={onNext}
                  className="text-sm bg-primary text-white px-4 py-1 rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {t('common.next')}
                </button>
              ) : (
                <button
                  onClick={onComplete}
                  className="text-sm bg-primary text-white px-4 py-1 rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {t('common.completed')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingOverlay; 